import React from 'react';
import cn from 'classnames';
import { parseNewLines } from '@alfa-bank/site-helpers-common/react/parse-new-lines';
import type { TTag } from '@alfa-bank/site-text-v2/src/models';

import type { TCardIcon, ITopElement } from './models';
import { mapHeadingSizeToTextView } from './config';
import { ArrowIcon } from './components/arrow-icon';

export const CardIcon = ({
    Block,
    Control,
    DataLayer,
    Img,
    Label,
    Link,
    Text,
    analyticsClick,
    backgroundColor,
    backgroundImage,
    className,
    headingPriority,
    headingSize = 4,
    headingSize22 = false,
    headingTopMargin = true,
    hoverEffect = true,
    id,
    isSpecial = false,
    linkText,
    linkUrl,
    styles,
    subtitle,
    themeColor = 'white',
    title,
    titleStyler,
    topElement,
    width,
    withModalControl,
    withoutBorder = false,
}: TCardIcon) => {
    const renderTopElement = ({ imgWidth = '100%', type, value }: ITopElement) => {
        switch (type) {
            case 'icon':
                return <Img alt="иконка карточки" height="40" src={value} width="40" />;
            case 'image':
                return <Img alt="картинка карточки" height="40" src={value} width={imgWidth} />;
            case 'text':
                return (
                    <Text styler={{ margin: 0 }} theme={themeColor} view="primary-small">
                        {value}
                    </Text>
                );
            case 'label':
                return <Label text={value} uppercase />;
            /* istanbul ignore next */
            default:
                return null;
        }
    };

    const headingPriorityTag = (headingPriority ? `h${headingPriority}` : undefined) as TTag;

    const content = (
        <Block
            borderColor={backgroundImage || withoutBorder ? undefined : 'dark-indigo-10'}
            className={cn(styles.external, styles.card, {
                [styles.cardSpecial]: isSpecial,
                [styles.inactive]: !hoverEffect,
                className,
                [styles.modalControl]: withModalControl,
            })}
            fullHeight
            id={id ? String(id) : undefined}
            style={{
                backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
                backgroundColor,
                width,
            }}
        >
            <div className={styles.content}>
                <div>
                    <div className={styles.iconBlock}>{renderTopElement(topElement)}</div>
                    <div
                        className={cn({
                            [styles.textBlock]: headingTopMargin,
                        })}
                    >
                        <Text
                            className={cn(styles.external, {
                                [styles.customFontSize]: headingSize22,
                                [styles.fontSize22]: headingSize22,
                            })}
                            styler={titleStyler}
                            tag={headingPriorityTag}
                            theme={themeColor}
                            view={mapHeadingSizeToTextView[headingSize]}
                        >
                            {parseNewLines(title)}
                        </Text>
                        {subtitle && (
                            <Text styler={{ margin: 0 }} theme={themeColor} view="primary-small">
                                {subtitle}
                            </Text>
                        )}
                    </div>
                </div>
                {linkText && hoverEffect && (
                    <div className={styles.link}>
                        <div className={styles.linkContent}>
                            <Text
                                className={cn(styles.external, styles.linkText)}
                                styler={{ margin: 0 }}
                                view="component-primary"
                            >
                                {linkText}
                            </Text>
                            <ArrowIcon
                                className={cn(styles.external, styles.arrow)}
                                height={14}
                                width={20}
                            />
                        </div>
                    </div>
                )}
            </div>
        </Block>
    );

    return (
        <DataLayer data={analyticsClick}>
            {withModalControl && <Control>{content}</Control>}
            {linkUrl && !withModalControl && (
                <Link href={linkUrl} noUnderline>
                    {content}
                </Link>
            )}
            {!linkUrl && !withModalControl && content}
        </DataLayer>
    );
};
